import { useEffect } from "react";

const setError = (CtxDispatch, name, value) =>
	CtxDispatch({ type: "SET_TASK_ERROR", payload: { name, value } });

const dispatchErrors = (CtxDispatch, errors) =>
	Object.entries(errors).forEach(([name, value]) => setError(CtxDispatch, name, value));

const intervalError = (hasNoIntervals, plural) => (hasNoIntervals ? `No ${plural} Assigned` : "");
const stageError = (hasNoStages, plural) => (hasNoStages ? `No ${plural} Assigned` : "");
const roleError = (hasNoRoles, plural) => (hasNoRoles ? `No ${plural} Assigned` : "");
const estimatedMinutesError = (minutes) => (+minutes <= 0 ? "Invalid Estimated Minutes" : "");
const zoneError = (hasNoZones, plural) => (hasNoZones ? `No ${plural} Assigned` : "");

// The authoritative check, once the task detail (taskInfo) has loaded.
const taskInfoErrors = (taskInfo, stageList, customCaptions) => {
	const hasNoRoles =
		taskInfo?.roles === null || taskInfo?.roles?.filter((r) => r.id !== null).length === 0;
	const hasZoneableStage = stageList.some((x) => x.hasZones && x.id !== null);
	const hasNoZones = hasZoneableStage && taskInfo.stageCount !== 0 && taskInfo.zoneCount === 0;
	return {
		interval: intervalError(taskInfo?.intervalCount === 0 || 0, customCaptions?.intervalPlural),
		stage: stageError(taskInfo?.stageCount === 0 || 0, customCaptions?.stagePlural),
		role: roleError(hasNoRoles, customCaptions?.rolePlural),
		estimatedMinutes: estimatedMinutesError(taskInfo.estimatedMinutes),
		zone: zoneError(hasNoZones, customCaptions?.zonePlural),
	};
};

// A best-effort check from the summary row, before the task detail has loaded.
const rowErrors = (row, originalRow, customCaptions) => ({
	interval: intervalError(!row?.intervals, customCaptions?.intervalPlural),
	stage: stageError(!row?.stages, customCaptions?.stagePlural),
	role: roleError(!row?.roles, customCaptions?.rolePlural),
	estimatedMinutes: estimatedMinutesError(row?.estimatedMinutes),
	zone: zoneError(
		!originalRow?.stages && !row?.zones && !row?.stages,
		customCaptions?.zonePlural
	),
});

// Two passes over the same fields, dispatched to the same task-error state.
// The row-based pass runs before the task detail has loaded, using only what
// the summary row already has; the taskInfo-based pass re-checks with the
// authoritative detail once it arrives and supersedes it.
const useTaskRowValidationErrors = ({ row, originalRow, taskInfo, stageList, customCaptions, CtxDispatch }) => {
	useEffect(() => {
		dispatchErrors(CtxDispatch, taskInfoErrors(taskInfo, stageList, customCaptions));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		row,
		CtxDispatch,
		originalRow,
		taskInfo.stageCount,
		taskInfo.estimatedMinutes,
		taskInfo.intervalCount,
		taskInfo.roles,
		stageList,
		taskInfo.zoneCount,
	]);

	useEffect(() => {
		dispatchErrors(CtxDispatch, rowErrors(row, originalRow, customCaptions));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [row, CtxDispatch, originalRow]);
};

export default useTaskRowValidationErrors;
